import React, { useEffect, useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { Alert, Box, List, ListItemButton, ListItemText, Typography } from '@mui/material';
import NkySpinner from '../common/NkySpinner/NkySpinner';
import {
  StyledUsersDialog,
  UsersDialogTitle,
  UsersDialogCloseButton,
  UsersDialogContent,
  UsersDialogActions,
  UsersField,
  UsersPrimaryButton,
  UsersSecondaryButton,
  UsersPanel,
} from './AdminUsers.styled';

function platformBusinessId(business) {
  return business?.BusinessId || business?.id || '';
}

function accountDisplayName(account) {
  return `${account?.FirstName || ''} ${account?.LastName || ''}`.trim() || 'this user';
}

function AdminAssignBusinessModal({
  open,
  account,
  businesses = [],
  businessesLoading = false,
  businessesError = null,
  onClose,
  onAssign,
  onUnlink,
  saving = false,
}) {
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [error, setError] = useState(null);

  const isAdminAccount = account?.Role === 'admin_user';
  const currentBusinessId = account?.BusinessId || '';

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setSelectedId(currentBusinessId);
    setError(null);
  }, [open, currentBusinessId]);

  const sortedBusinesses = useMemo(
    () =>
      [...businesses].sort((a, b) =>
        String(a.BusinessName || '').localeCompare(
          String(b.BusinessName || ''),
          undefined,
          { sensitivity: 'base' },
        ),
      ),
    [businesses],
  );

  const filteredBusinesses = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sortedBusinesses;
    return sortedBusinesses.filter((business) => {
      const haystack = [
        business.BusinessName,
        platformBusinessId(business),
        business.BusinessEmail,
        business.BusinessPhoneNumber,
        business.BusinessAddress?.City,
        business.BusinessAddress?.State,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [sortedBusinesses, search]);

  const currentBusinessName = useMemo(() => {
    if (!currentBusinessId) return '';
    return (
      sortedBusinesses.find(
        (business) => platformBusinessId(business) === currentBusinessId,
      )?.BusinessName || currentBusinessId
    );
  }, [sortedBusinesses, currentBusinessId]);

  const handleAssign = async () => {
    if (isAdminAccount) {
      setError('Administrator accounts cannot be assigned to a business.');
      return;
    }
    if (!selectedId) {
      setError('Select a business to assign this user to.');
      return;
    }
    setError(null);
    try {
      await onAssign(selectedId);
    } catch (e) {
      setError(e.message || 'Failed to assign user to business');
    }
  };

  const handleUnlink = async () => {
    setError(null);
    try {
      await onUnlink();
    } catch (e) {
      setError(e.message || 'Failed to remove user from business');
    }
  };

  return (
    <StyledUsersDialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="admin-assign-business-title"
    >
      <UsersDialogTitle id="admin-assign-business-title">
        <span className="users-dialog-icon-wrap" aria-hidden>
          <StorefrontOutlinedIcon sx={{ color: '#0d4efe' }} />
        </span>
        Add to a business
        <UsersDialogCloseButton
          aria-label="Close"
          onClick={onClose}
          disabled={saving}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </UsersDialogCloseButton>
      </UsersDialogTitle>

      <UsersDialogContent>
        <Typography variant="body2" sx={{ color: '#5a6478' }}>
          Assign{' '}
          <Box component="span" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
            {accountDisplayName(account)}
          </Box>
          {account?.Email ? ` (${account.Email})` : ''} to a platform business.
        </Typography>

        {currentBusinessName && (
          <Typography variant="body2" sx={{ color: '#5a6478' }}>
            Currently assigned to{' '}
            <Box component="span" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
              {currentBusinessName}
            </Box>
            .
          </Typography>
        )}

        {isAdminAccount && (
          <Alert severity="info" sx={{ borderRadius: '10px' }}>
            Administrator accounts cannot be assigned to a business. Use
            Business Management if you need to act as a provider.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ borderRadius: '10px' }}>
            {error}
          </Alert>
        )}

        <UsersField
          autoFocus
          fullWidth
          size="small"
          label="Search businesses"
          placeholder="Name, email, city…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          disabled={isAdminAccount}
        />

        <UsersPanel sx={{ p: 0, overflow: 'hidden' }}>
          <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
            {businessesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <NkySpinner />
              </Box>
            ) : businessesError ? (
              <Typography sx={{ color: '#5a6478', p: 2 }}>
                {businessesError}
              </Typography>
            ) : filteredBusinesses.length === 0 ? (
              <Typography sx={{ color: '#5a6478', p: 2 }}>
                No businesses match “{search.trim() || 'your search'}”.
              </Typography>
            ) : (
              <List disablePadding dense>
                {filteredBusinesses.map((business) => {
                  const id = platformBusinessId(business);
                  const selected = selectedId === id;
                  const city = business.BusinessAddress?.City;
                  return (
                    <ListItemButton
                      key={id}
                      selected={selected}
                      disabled={saving || isAdminAccount}
                      onClick={() => setSelectedId(id)}
                      sx={{
                        borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(13, 78, 254, 0.08)',
                        },
                        '&.Mui-selected:hover': {
                          backgroundColor: 'rgba(13, 78, 254, 0.12)',
                        },
                      }}
                    >
                      <ListItemText
                        primary={business.BusinessName || id}
                        secondary={
                          [city, business.BusinessEmail].filter(Boolean).join(' · ') ||
                          null
                        }
                        primaryTypographyProps={{ fontWeight: selected ? 700 : 600 }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            )}
          </Box>
        </UsersPanel>
      </UsersDialogContent>

      <UsersDialogActions>
        {Boolean(currentBusinessId) && !isAdminAccount && (
          <UsersSecondaryButton
            color="error"
            variant="outlined"
            disabled={saving}
            onClick={handleUnlink}
            sx={{
              mr: 'auto',
              borderColor: 'rgba(211,47,47,0.4)',
              color: '#d32f2f',
            }}
          >
            Remove from business
          </UsersSecondaryButton>
        )}
        <UsersSecondaryButton variant="outlined" onClick={onClose} disabled={saving}>
          Cancel
        </UsersSecondaryButton>
        <UsersPrimaryButton
          onClick={handleAssign}
          disabled={saving || isAdminAccount || !selectedId}
        >
          {saving ? 'Saving…' : 'Add to business'}
        </UsersPrimaryButton>
      </UsersDialogActions>
    </StyledUsersDialog>
  );
}

export default AdminAssignBusinessModal;
